import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import type { LatLngExpression } from 'leaflet'
import { Camera, Droplets, Image as ImageIcon, Info, Layers, Ruler, Mountain } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../hooks/useTheme'
import floodPolesData from '../data/flood-poles-data.json'
import PhotoUpload from './PhotoUpload'
import PhotoTimeline from './PhotoTimeline'
import { apiUrl } from '../lib/api'

interface FloodPole {
  id: number
  code: string
  location: string
  location_en: string
  base_level: number
  max_flood_level: number
  flood_height_from_base: number
  coordinates: {
    lat: number
    lng: number
  }
  image_url: string
  series: string
}

interface FloodZoneMapProps {
  currentWaterLevel?: number
  className?: string
}

const ChiangMai: LatLngExpression = [18.7883, 98.9853]

const RecenterOnTheme: React.FC<{ theme: 'light' | 'dark' }> = ({ theme }) => {
  const map = useMap()
  useEffect(() => {
    map.setView(ChiangMai)
  }, [map, theme])
  return null
}

const FloodZoneMap: React.FC<FloodZoneMapProps> = ({ 
  currentWaterLevel = 300.5, 
  className = "" 
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [photos, setPhotos] = useState<{ id: string, lat: number, lng: number, createdAt: number, expiresAt: number }[]>([])
  const [showUpload, setShowUpload] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'map' | 'photos'>('map')
  const photoCount = photos.length

  const mapAttribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors & ' +
    '<a href="https://carto.com/attributions">CARTO</a>'
  const lightTiles = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
  const darkTiles = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
  const openTopoTiles = 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'
  const openTopoAttribution = 'Map data: &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://www2.jpl.nasa.gov/srtm/">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
  const [waterLevel, setWaterLevel] = useState<number>(currentWaterLevel)
  const [showFloodZones, setShowFloodZones] = useState<boolean>(true)
  const [showTerrain, setShowTerrain] = useState<boolean>(false)

  // Reference levels and terminology:
  // - Absolute water level: Measured from sea level datum (standard geodetic reference)
  // - Relative water level: Measured from Zerogate baseline (300.5m above sea level)
  // - Zerogate: The baseline reference point at 300.5m above sea level
  const ZEROGATE_LEVEL = 300.5  // Zerogate baseline level (300.5m above sea level)
  const WARNING_LEVEL = 303.6   // Warning water level (3.1m relative from Zerogate)
  const CRITICAL_LEVEL = 304.2  // Critical water level (3.7m relative from Zerogate)
  const OVERFLOW_LEVEL = 304.7  // Overflow level (4.2m relative from Zerogate)
  const FLOOD_2024_LEVEL = 305.78  // 2024 flood level (5.28m relative from Zerogate)

  // Process flood poles data
  const floodPoles: FloodPole[] = useMemo(() => {
    return floodPolesData.flood_poles || []
  }, [])

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await fetch(apiUrl('/api/photos/list'))
      if (!res.ok) return
      const data = await res.json()
      setPhotos(Array.isArray(data.photos) ? data.photos : [])
    } catch {}
  }, [])

  useEffect(() => {
    fetchPhotos()
    const id = setInterval(fetchPhotos, 60000)
    return () => clearInterval(id)
  }, [fetchPhotos])

  // Upload handled by external component

  // Get flood zone status for each pole
  const getFloodStatus = useCallback((pole: FloodPole) => {
    // Only consider flood risk when river overflows (reaches overflow level)
    if (waterLevel < OVERFLOW_LEVEL) {
      return 'safe'
    }
    
    const poleFloodLevel = pole.base_level + pole.flood_height_from_base
    
    if (waterLevel >= poleFloodLevel) {
      return 'flooded'
    } else if (waterLevel >= (poleFloodLevel - 0.5)) {
      return 'warning'
    }
    return 'safe'
  }, [waterLevel, OVERFLOW_LEVEL])

  // Get water level status color
  const getWaterLevelColor = () => {
    if (waterLevel >= CRITICAL_LEVEL) return 'text-red-600 dark:text-red-400'
    if (waterLevel >= WARNING_LEVEL) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-green-600 dark:text-green-400'
  }

  // Get slider color based on water level
  const getSliderColor = () => {
    if (waterLevel >= CRITICAL_LEVEL) return '#ef4444' // red
    if (waterLevel >= WARNING_LEVEL) return '#f59e0b' // yellow/orange
    return '#10b981' // green
  }

  const relativeWaterLevel = waterLevel - ZEROGATE_LEVEL

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b">
        {/* Top row: title + inline tabs on desktop, actions on the right */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          {/* Title + Tabs (desktop) */}
          <div className="flex items-center justify-between sm:justify-start">
            <div className="flex items-center space-x-2">
              <Droplets className="h-6 w-6 text-blue-500" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">{t('floodZoneMap.title')}</h3>
            </div>
            {/* Tabs inline on desktop */}
            <div className="hidden sm:block ml-3">
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700/50 rounded-full p-1 border border-gray-200 dark:border-gray-700">
                <button
                  aria-pressed={activeTab === 'map'}
                  className={`flex items-center space-x-1 px-3 py-1 text-sm rounded-full transition-colors ${activeTab === 'map' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-800/70'}`}
                  onClick={() => setActiveTab('map')}
                >
                  <Layers className="h-4 w-4" />
                  <span>Map</span>
                </button>
                <button
                  aria-pressed={activeTab === 'photos'}
                  className={`ml-1 flex items-center space-x-1 px-3 py-1 text-sm rounded-full transition-colors ${activeTab === 'photos' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-800/70'}`}
                  onClick={() => setActiveTab('photos')}
                >
                  <ImageIcon className="h-4 w-4" />
                  <span>Photos</span>
                  {photoCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] bg-blue-600 text-white">{photoCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Actions - desktop inline */}
          <div className="hidden sm:flex items-center space-x-4">
            <button
              onClick={() => setShowTerrain(!showTerrain)}
              className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm transition-colors ${
                showTerrain 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
              }`}
            >
              <Mountain className="h-4 w-4" />
              <span>{t('floodZoneMap.terrain')}</span>
            </button>
            <button
              onClick={() => setShowFloodZones(!showFloodZones)}
              className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm transition-colors ${
                showFloodZones 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
              }`}
            >
              <Layers className="h-4 w-4" />
              <span>{t('floodZoneMap.floodZones')}</span>
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center space-x-1 px-3 py-1 rounded-md text-sm transition-colors bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
            >
              <Camera className="h-4 w-4" />
              <span>Share Photo</span>
            </button>
          </div>
        </div>

        {/* Mobile-only: Tabs full-width */}
        <div className="sm:hidden mb-2">
          <div className="flex items-center bg-gray-100 dark:bg-gray-700/50 rounded-full p-1 border border-gray-200 dark:border-gray-700 w-full">
            <button
              aria-pressed={activeTab === 'map'}
              className={`flex-1 flex items-center justify-center space-x-1 px-3 py-1 text-xs rounded-full transition-colors ${activeTab === 'map' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-700 dark:text-gray-200'}`}
              onClick={() => setActiveTab('map')}
            >
              <Layers className="h-4 w-4" />
              <span>Map</span>
            </button>
            <button
              aria-pressed={activeTab === 'photos'}
              className={`flex-1 ml-1 flex items-center justify-center space-x-1 px-3 py-1 text-xs rounded-full transition-colors ${activeTab === 'photos' ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-700 dark:text-gray-200'}`}
              onClick={() => setActiveTab('photos')}
            >
              <ImageIcon className="h-4 w-4" />
              <span>Photos</span>
              {photoCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] bg-blue-600 text-white">{photoCount}</span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile-only: Actions in 2-column grid */}
        <div className="sm:hidden grid grid-cols-3 gap-2 mb-3">
          <button
            onClick={() => setShowTerrain(!showTerrain)}
            className={`flex items-center justify-center space-x-1 px-2 py-2 rounded-md text-xs transition-colors ${
              showTerrain 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            <Mountain className="h-4 w-4" />
            <span>{t('floodZoneMap.terrain')}</span>
          </button>
          <button
            onClick={() => setShowFloodZones(!showFloodZones)}
            className={`flex items-center justify-center space-x-1 px-2 py-2 rounded-md text-xs transition-colors ${
              showFloodZones 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            <Layers className="h-4 w-4" />
            <span>{t('floodZoneMap.floodZones')}</span>
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center justify-center space-x-1 px-2 py-2 rounded-md text-xs transition-colors bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
          >
            <Camera className="h-4 w-4" />
            <span>Share Photo</span>
          </button>
        </div>

        {/* Water Level Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('floodZoneMap.waterLevelSimulation')}
            </label>
            <div className="flex items-center space-x-2">
              <Ruler className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className={`font-semibold ${getWaterLevelColor()}`}>
                {relativeWaterLevel >= 0 ? '+' : ''}{relativeWaterLevel.toFixed(2)}m
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400" title={t('waterLevel.explanations.absoluteLevel')}>
                ({waterLevel.toFixed(2)}m {t('floodZoneMap.absolute')})
              </span>
            </div>
          </div>
          
          <div className="relative">
            <input
              type="range"
              min={ZEROGATE_LEVEL}
              max={FLOOD_2024_LEVEL}
              step={0.1}
              value={waterLevel}
              onChange={(e) => setWaterLevel(parseFloat(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, 
                  #10b981 0%, 
                  #10b981 ${((WARNING_LEVEL - ZEROGATE_LEVEL) / (FLOOD_2024_LEVEL - ZEROGATE_LEVEL)) * 100}%, 
                  #f59e0b ${((WARNING_LEVEL - ZEROGATE_LEVEL) / (FLOOD_2024_LEVEL - ZEROGATE_LEVEL)) * 100}%, 
                  #f59e0b ${((OVERFLOW_LEVEL - ZEROGATE_LEVEL) / (FLOOD_2024_LEVEL - ZEROGATE_LEVEL)) * 100}%, 
                  #ef4444 ${((OVERFLOW_LEVEL - ZEROGATE_LEVEL) / (FLOOD_2024_LEVEL - ZEROGATE_LEVEL)) * 100}%, 
                  #ef4444 100%)`
              }}
            />
            
            {/* Reference level markers */}
            <div className="mt-2 flex justify-between text-gray-500 dark:text-gray-400" style={{ fontSize: '10px', lineHeight: '1.2' }}>
              <span>{t('floodZoneMap.zerogate')}<br/>{ZEROGATE_LEVEL}m</span>
              <span>{t('floodZoneMap.warning')}<br/>{WARNING_LEVEL}m</span>
              <span>{t('floodZoneMap.critical')}<br/>{CRITICAL_LEVEL}m</span>
              <span>Overflow<br/>{OVERFLOW_LEVEL}m</span>
              <span>{t('floodZoneMap.flood2024')}<br/>{FLOOD_2024_LEVEL}m</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-lg font-bold text-green-600 dark:text-green-400">
              {floodPoles.filter(p => getFloodStatus(p) === 'safe').length}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400">{t('floodZoneMap.safePoles')}</div>
          </div>
          <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
              {floodPoles.filter(p => getFloodStatus(p) === 'warning').length}
            </div>
            <div className="text-xs text-yellow-600 dark:text-yellow-400">{t('floodZoneMap.warningPoles')}</div>
          </div>
          <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-lg font-bold text-red-600 dark:text-red-400">
              {floodPoles.filter(p => getFloodStatus(p) === 'flooded').length}
            </div>
            <div className="text-xs text-red-600 dark:text-red-400">{t('floodZoneMap.floodedPoles')}</div>
          </div>
        </div>
      </div>

      {/* Tabs moved next to title */}

      {/* Content */}
      <div className="relative h-96">
        {activeTab === 'map' ? (
          showFloodZones ? (
            <MapContainer
              center={ChiangMai}
              zoom={13}
              style={{ width: '100%', height: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution={showTerrain ? openTopoAttribution : mapAttribution}
                url={showTerrain ? openTopoTiles : (theme === 'dark' ? darkTiles : lightTiles)}
              />
              <RecenterOnTheme theme={theme} />

              {floodPoles.map((pole) => {
                const status = getFloodStatus(pole)
                const color = status === 'flooded' ? '#ef4444' : status === 'warning' ? '#f59e0b' : '#10b981'
                const position: LatLngExpression = [pole.coordinates.lat, pole.coordinates.lng]
                const border = theme === 'dark' ? '#374151' : '#ffffff'
                const infoWindowBg = theme === 'dark' ? '#1f2937' : '#ffffff'
                const infoWindowText = theme === 'dark' ? '#f3f4f6' : '#1f2937'
                return (
                  <CircleMarker
                    key={pole.id}
                    center={position}
                    radius={8}
                    pathOptions={{ color: border, weight: 2, fillColor: color, fillOpacity: 0.8 }}
                  >
                    <Popup>
                      <div style={{ maxWidth: 250, backgroundColor: infoWindowBg, color: infoWindowText, padding: 4 }}>
                        <h4 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: infoWindowText }}>{pole.location_en}</h4>
                        <p style={{ margin: '4px 0', color: infoWindowText }}><strong>{t('floodZoneMap.infoWindow.code')}</strong> {pole.code}</p>
                        <p style={{ margin: '4px 0', color: infoWindowText }}><strong>{t('floodZoneMap.infoWindow.baseLevel')}</strong> {pole.base_level}m</p>
                        <p style={{ margin: '4px 0', color: infoWindowText }}><strong>{t('floodZoneMap.infoWindow.floodLevel')}</strong> {(pole.base_level + pole.flood_height_from_base).toFixed(2)}m</p>
                        <p style={{ margin: '4px 0', color: infoWindowText }}><strong>{t('floodZoneMap.infoWindow.currentWater')}</strong> {waterLevel.toFixed(2)}m</p>
                        <p style={{ margin: '4px 0', color: infoWindowText }}><strong>{t('floodZoneMap.infoWindow.status')}</strong>{' '}
                          <span style={{ color, fontWeight: 'bold', textTransform: 'capitalize' }}>{status}</span>
                        </p>
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              })}

              {photos.map((photo) => {
                const position: LatLngExpression = [photo.lat, photo.lng]
                const border = theme === 'dark' ? '#1e40af' : '#93c5fd'
                const infoWindowBg = theme === 'dark' ? '#1f2937' : '#ffffff'
                const infoWindowText = theme === 'dark' ? '#f3f4f6' : '#1f2937'
                return (
                  <CircleMarker
                    key={photo.id}
                    center={position}
                    radius={6}
                    pathOptions={{ color: border, weight: 2, fillColor: '#3b82f6', fillOpacity: 0.9 }}
                  >
                    <Popup>
                      <div style={{ maxWidth: 260, backgroundColor: infoWindowBg, color: infoWindowText, padding: 4 }}>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>{new Date(photo.createdAt).toLocaleString()}</div>
                        <img src={apiUrl(`/api/photos/image/${photo.id}`)} style={{ width: '100%', height: 'auto', borderRadius: 6 }} />
                        <div style={{ fontSize: 11, marginTop: 4, color: infoWindowText }}>
                          Expires in ~{Math.max(0, Math.round((photo.expiresAt - Date.now()) / (60 * 60 * 1000)))}h
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                )
              })}
            </MapContainer>
          ) : (
                        <div className="bg-gray-100 dark:bg-gray-600 h-full flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <Layers className="h-8 w-8 mx-auto mb-2" />
                <p>{t('floodZoneMap.mapHidden')}</p>
              </div>
            </div>
          )
        ) : (
          <PhotoTimeline photos={photos} onUploadClick={() => setShowUpload(true)} />
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowUpload(false)}></div>
          <div className="relative z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 w-[90vw] max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">Share a photo</div>
              <button onClick={() => setShowUpload(false)} className="text-sm text-gray-600 dark:text-gray-300">Close</button>
            </div>
            <PhotoUpload onClose={() => setShowUpload(false)} onUploaded={fetchPhotos} />
          </div>
        </div>
      )}

      {/* Legend */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
                          <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span className="font-medium text-gray-700 dark:text-gray-200">{t('floodZoneMap.legend')}</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{t('floodZoneMap.safe')} ({floodPoles.filter(p => getFloodStatus(p) === 'safe').length})</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{t('floodZoneMap.warning')} ({floodPoles.filter(p => getFloodStatus(p) === 'warning').length})</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">{t('floodZoneMap.flooded')} ({floodPoles.filter(p => getFloodStatus(p) === 'flooded').length})</span>
          </div>
        </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {t('floodZoneMap.basedOnCmu').replace('{count}', floodPoles.length.toString())}
        </div>
      </div>

      {/* Custom slider styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: ${getSliderColor()};
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            transition: background-color 0.2s ease;
          }
          
          .slider::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: ${getSliderColor()};
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            transition: background-color 0.2s ease;
          }
        `
      }} />
    </div>
  )
}

export default FloodZoneMap 